/**
 * Gestor de preload de recursos
 * @module classes/navigation/PreloadManager
 */

/**
 * Clase para gestionar el preload de páginas y templates
 * @class PreloadManager
 */
export class PreloadManager {
    /**
     * Constructor del PreloadManager
     * @param {ControllerLoader} controllerLoader - Instancia del ControllerLoader
     */
    constructor(controllerLoader) {
        this.controllerLoader = controllerLoader;
        /** @type {Map<string, string>} */
        this.templateCache = new Map();
        /** En dev: no cachear templates para que los .html editados se vean sin recargar la app completa */
        this.templatesSkipCache =
            typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
    }

    /**
     * Preload de todas las páginas
     * @async
     */
    async preloadPages() {
        const pagesToPreload = [
            { controller: 'login', template: 'page-login.html' },
            { controller: 'trip', template: 'page-trip.html' },
            { controller: 'explore', template: 'page-explore.html' },
            //{ controller: 'experience', template: 'page-experience.html' },
            { controller: 'profile', template: 'page-profile.html' },
            { controller: 'uploadField', template: 'page-upload.field.html' },
            { controller: 'uploadFile', template: 'page-upload.file.html' },
            { controller: 'uploadForm', template: 'page-upload.form.html' },
            { controller: 'uploadLink', template: 'page-upload.link.html' },
            { controller: 'uploadPayment', template: 'page-upload.payment.html' },
            { controller: 'uploadJobOffer', template: 'page-upload.joboffer.html' },
            { controller: 'reader', template: 'page-reader.html' },
            { controller: 'viewer', template: 'page-viewer.html' },
            { controller: 'challenge', template: 'page-challenge.html' }
        ];

        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.runPreload(pagesToPreload);
            }, { timeout: 5000 });
        } else {
            setTimeout(() => this.runPreload(pagesToPreload), 1000);
        }
    }

    /**
     * Ejecuta el preload de páginas
     * @async
     * @param {Array} pages - Array de páginas a preload
     */
    async runPreload(pages) {
        let preloadedCount = 0;

        for (const page of pages) {
            try {
                const [controllerResult, templateResult] = await Promise.allSettled([
                    this.controllerLoader.preloadController(page.controller),
                    this.preloadTemplate(page.template)
                ]);

                if (controllerResult.status === 'fulfilled' && templateResult.status === 'fulfilled') {
                    preloadedCount++;
                } else {
                }

                await new Promise(resolve => setTimeout(resolve, 100));

            } catch (error) {
            }
        }
    }

    /**
     * Preload de un template
     * @async
     * @param {string} templateFile - Nombre del archivo template
     * @returns {Promise<string>} Contenido del template
     */
    async preloadTemplate(templateFile) {
        if (!this.templatesSkipCache && this.templateCache.has(templateFile)) {
            return this.templateCache.get(templateFile);
        }

        try {
            const url = this.templatesSkipCache ? `${templateFile}?reload` : templateFile;
            const response = await fetch(url, {
                cache: this.templatesSkipCache ? 'no-store' : 'default',
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const html = await response.text();
            if (!this.templatesSkipCache) {
                this.templateCache.set(templateFile, html);
            }
            return html;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene un template del cache o lo carga
     * @async
     * @param {string} templateFile - Nombre del archivo template
     * @returns {Promise<string>} Contenido del template
     */
    async getTemplate(templateFile) {
        if (!this.templatesSkipCache && this.templateCache.has(templateFile)) {
            return this.templateCache.get(templateFile);
        }

        return await this.preloadTemplate(templateFile);
    }
}

